import React from "react";
import { IonAvatar, IonButton, IonIcon, IonSpinner, IonText } from "@ionic/react";
import { peopleOutline } from "ionicons/icons";
import { Crew } from "../../context/CrewContext";
import { ResolvedCrewMember } from "../../hooks/useCrewMemberEditor";

interface CrewDetailPanelProps {
  selectedCrew: Crew | null;
  crewMembersResolved: ResolvedCrewMember[];
  getInitials: (name: string) => string;
  onEditCrewDetails: () => void;
  onOpenEditMembers: () => void;
  onDeleteCrew: () => void;
  deletingCrew: boolean;
  savingCrew: boolean;
  savingMembers: boolean;
  loading?: boolean;
}

export const CrewDetailPanel: React.FC<CrewDetailPanelProps> = ({
  selectedCrew,
  crewMembersResolved,
  getInitials,
  onEditCrewDetails,
  onOpenEditMembers,
  onDeleteCrew,
  deletingCrew,
  savingCrew,
  savingMembers,
  loading = false,
}) => {
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <IonSpinner />
      </div>
    );
  }

  if (!selectedCrew) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--ion-color-medium)",
          padding: "40px",
          textAlign: "center",
        }}
      >
        <IonIcon
          icon={peopleOutline}
          style={{ fontSize: "64px", marginBottom: "16px" }}
        />
        <h3 style={{ margin: "0 0 8px" }}>Select a Crew</h3>
        <p style={{ margin: 0 }}>Choose a crew from the list to manage its details and members.</p>
      </div>
    );
  }

  const busy = deletingCrew || savingCrew || savingMembers;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        padding: "32px",
        backgroundColor: "var(--ion-background-color)",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <IonAvatar
          style={{
            width: "120px",
            height: "120px",
            margin: "0 auto 16px",
          }}
        >
          {selectedCrew.avatarUrl ? (
            <img src={selectedCrew.avatarUrl} alt={selectedCrew.name} />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "32px",
                background: "#e5e5e5",
                color: "#333",
              }}
            >
              {getInitials(selectedCrew.name)}
            </div>
          )}
        </IonAvatar>
        <h1 style={{ margin: "0 0 8px", fontSize: "1.8rem" }}>{selectedCrew.name}</h1>
        <p style={{ color: "var(--ion-color-medium)", fontSize: "1rem", margin: 0 }}>
          {selectedCrew.memberCount} member{selectedCrew.memberCount === 1 ? "" : "s"}
        </p>
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <IonButton
          expand="block"
          fill="outline"
          onClick={onEditCrewDetails}
          style={{ flex: 1 }}
          disabled={busy}
        >
          Edit Details
        </IonButton>
        <IonButton
          expand="block"
          color="secondWarning"
          onClick={onOpenEditMembers}
          style={{ flex: 1 }}
          disabled={busy}
        >
          Members +/-
        </IonButton>
      </div>

      <IonButton
        expand="block"
        color="danger"
        fill="outline"
        onClick={onDeleteCrew}
        disabled={busy}
      >
        Delete Crew
      </IonButton>

      <div style={{ marginTop: "32px" }}>
        <h3 style={{ margin: "0 0 12px" }}>Description</h3>
        <IonText color="medium">
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            {selectedCrew.description && selectedCrew.description.trim().length > 0
              ? selectedCrew.description
              : "No description"}
          </p>
        </IonText>
      </div>

      <div style={{ marginTop: "32px" }}>
        <h3 style={{ margin: "0 0 12px" }}>Members</h3>
        {crewMembersResolved.length === 0 ? (
          <IonText color="medium">
            <p style={{ margin: 0 }}>No members yet.</p>
          </IonText>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {crewMembersResolved.map((member) => {
              const name = `${member.first} ${member.last}`.trim() || "Associate";
              return (
                <div
                  key={member.uid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 0",
                    borderBottom: "1px solid var(--ion-color-light-shade)",
                  }}
                >
                  <IonAvatar
                    style={{ width: "44px", height: "44px", flexShrink: 0 }}
                  >
                    {member.avatar ? (
                      <img src={member.avatar} alt={name} />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "14px",
                          background: "#e5e5e5",
                          color: "#333",
                        }}
                      >
                        {getInitials(name)}
                      </div>
                    )}
                  </IonAvatar>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <IonText color="medium">
                      <div style={{ fontSize: "0.92rem" }}>
                        {member.company ? member.company : member.found ? "" : "Not in your associates list"}
                      </div>
                    </IonText>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
